import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { PasswordStrength, scorePassword } from "@/components/auth/PasswordStrength";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type PlanChoice = "starter" | "pro";

export default function Signup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signup, status, user } = useAuth();

  const initialPlan = (params.get("plan") as PlanChoice | null) ?? "starter";
  const [plan, setPlan] = useState<PlanChoice>(
    initialPlan === "pro" ? "pro" : "starter"
  );
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status === "authenticated" && user) {
      navigate(user.onboarding_completed ? "/app" : "/onboarding", {
        replace: true,
      });
    }
  }, [status, user, navigate]);

  const passwordOk = useMemo(() => scorePassword(password).score >= 1, [password]);
  const canSubmit =
    fullName.trim().length > 0 &&
    email.trim().length > 0 &&
    passwordOk &&
    password === confirm &&
    !submitting;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await signup({
        email: email.trim(),
        password,
        full_name: fullName.trim(),
        plan,
      });
      navigate("/onboarding", { replace: true });
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Signup failed.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      testId="signup-page"
      eyebrow="Start your trial"
      heading="14 days. No credit card. No spreadsheets."
      description={
        <>
          Already have an account?{" "}
          <Link
            to="/login"
            data-testid="signup-link-login"
            className="text-oxblood hover:underline underline-offset-4"
          >
            Sign in
          </Link>
          .
        </>
      }
    >
      <form
        data-testid="signup-form"
        onSubmit={onSubmit}
        className="space-y-5"
        noValidate
      >
        <fieldset className="grid grid-cols-2 gap-3">
          <legend className="sr-only">Plan</legend>
          {(["starter", "pro"] as PlanChoice[]).map((p) => (
            <label
              key={p}
              data-testid={`signup-plan-${p}`}
              className={`cursor-pointer border px-4 py-3 transition-colors ${
                plan === p
                  ? "border-oxblood bg-oxblood/5"
                  : "border-border hover:border-foreground/40"
              }`}
            >
              <input
                type="radio"
                name="plan"
                value={p}
                checked={plan === p}
                onChange={() => setPlan(p)}
                className="sr-only"
              />
              <span className="block text-sm font-medium capitalize">
                {p}
              </span>
              <span className="block text-xs text-muted-foreground mt-1">
                {p === "starter" ? "$8 / month" : "$90 / month"}
              </span>
            </label>
          ))}
        </fieldset>

        <div className="space-y-1.5">
          <Label htmlFor="full_name">Full name</Label>
          <Input
            id="full_name"
            data-testid="signup-fullname"
            value={fullName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFullName(e.target.value)
            }
            autoComplete="name"
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email">Work email</Label>
          <Input
            id="email"
            type="email"
            data-testid="signup-email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(e.target.value)
            }
            autoComplete="email"
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            data-testid="signup-password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)
            }
            autoComplete="new-password"
            required
            minLength={8}
          />
          <PasswordStrength password={password} />
          <p className="text-xs text-muted-foreground">
            At least 8 characters with letters and numbers.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="confirm">Confirm password</Label>
          <Input
            id="confirm"
            type="password"
            data-testid="signup-confirm"
            value={confirm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setConfirm(e.target.value)
            }
            autoComplete="new-password"
            required
            minLength={8}
          />
          {confirm && confirm !== password && (
            <p
              data-testid="signup-mismatch"
              className="text-xs text-oxblood mt-1"
            >
              Passwords don't match.
            </p>
          )}
        </div>

        {error && (
          <p
            role="alert"
            data-testid="signup-error"
            className="text-sm text-oxblood border border-oxblood/30 bg-oxblood/5 px-3 py-2"
          >
            {error}
          </p>
        )}

        <Button
          type="submit"
          size="lg"
          data-testid="signup-submit"
          disabled={!canSubmit}
          className="w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-12"
        >
          {submitting ? "Creating account…" : "Start 14-Day Free Trial"}
        </Button>

        <p className="text-[11px] text-muted-foreground text-center tracking-wide">
          By continuing you agree to our Terms and Privacy Policy.
        </p>
      </form>
    </AuthLayout>
  );
}
